import React, { useState, useEffect } from 'react';
import { Menu, X, User, Settings, LogOut, Activity, FileText, Calendar, BadgeCheck, BookOpen, Bell, Newspaper, Radar, GanttChartSquare, FolderOpen, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Logo } from './Logo';
import { apiService } from '../services/api';

interface HeaderProps {}

const Header: React.FC<HeaderProps> = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isResourcesMenuOpen, setIsResourcesMenuOpen] = useState(false);
  const [isResourcesHovered, setIsResourcesHovered] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [trialStatus, setTrialStatus] = useState<string | null>(null);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);

  useEffect(() => {
    // Fetch current user info
    const fetchUser = async () => {
      try {
        const user = await apiService.getCurrentUser();
        if (user && user.email) {
          setUserEmail(user.email);
        }
        setIsSuperuser(Boolean(user?.is_superuser));
        setTrialStatus(user?.trial_status ?? null);
        setTrialEnd(user?.trial_end ?? null);
      } catch (err: any) {
        // User not authenticated or error - this is okay, just don't show email
        if (err.response?.status !== 401) {
          console.error('Error fetching user:', err);
        }
      }
    };
    fetchUser();
  }, []);

  // Close profile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isProfileMenuOpen && !(event.target as Element).closest('.profile-menu-container')) {
        setIsProfileMenuOpen(false);
      }
      if (isResourcesMenuOpen && !(event.target as Element).closest('.resources-menu-container')) {
        setIsResourcesMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen, isResourcesMenuOpen]);

  const handleLogout = async () => {
    try {
      // Clear frontend state first
      setUserEmail('');
      sessionStorage.clear();
      localStorage.clear(); // Also clear localStorage
      
      // Call logout API
      await apiService.logout();
      
      // Wait a moment to ensure cookies are cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force a full page reload to ensure all state is cleared
      // Use window.location.replace to prevent back button issues
      window.location.replace('/login');
    } catch (err) {
      console.error('Logout error:', err);
      // Clear all state even if logout API fails
      setUserEmail('');
      sessionStorage.clear();
      localStorage.clear();
      
      // Force redirect to login
      window.location.replace('/login');
    }
  };

  const formatTrialEnd = (value: string | null) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-white border-b border-gray-100">
      <div className="w-full max-w-[100%] mx-auto px-3 sm:px-4 lg:px-6">
        {trialStatus === 'active' && (
          <div className="mt-3 mb-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-700 flex items-center justify-between gap-3">
            <span>
              Demo mode active{formatTrialEnd(trialEnd) ? ` · Ends ${formatTrialEnd(trialEnd)}` : ''}
            </span>
            <span className="font-semibold">Limited access</span>
          </div>
        )}
        <div className="flex flex-col min-h-14 py-2 gap-1.5">
          <div className="flex justify-between items-center gap-2">
            {/* Logo Section */}
            <Link to="/newsfeed" className="flex-shrink-0 flex items-center cursor-pointer">
              <Logo />
            </Link>

            {/* Right Section (Profile). Kept on the logo's row so it's always visible. */}
            <div className="hidden xl:flex items-center gap-1 shrink-0 relative z-20 bg-white pl-1">
               <div className="pl-2 border-l border-gray-200 flex items-center gap-2">
                  <div className="text-right hidden min-[1440px]:block max-w-[9rem]">
                      <p className="text-sm font-bold text-slate-900 truncate" title={userEmail || 'Account'}>{userEmail || 'User'}</p>
                      <p className="text-[10px] uppercase tracking-widest font-semibold text-indigo-600">Account</p>
                  </div>
                  <div className="relative profile-menu-container">
                    <button
                      onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
                      className="h-10 w-10 rounded-full bg-gradient-to-tr from-slate-100 to-slate-200 ring-2 ring-white shadow-sm flex items-center justify-center overflow-hidden hover:ring-indigo-200 transition-all"
                    >
                      <User className="w-5 h-5 text-slate-500" />
                    </button>

                    {/* Profile Dropdown Menu */}
                    {isProfileMenuOpen && (
                      <div className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden bg-white/95 backdrop-blur-sm z-50">
                        <div className="py-1">
                          <Link
                            to="/account"
                            onClick={() => setIsProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-slate-700"
                          >
                            <Settings className="w-4 h-4" />
                            <span className="text-sm font-medium">Account Settings</span>
                          </Link>
                          <button
                            onClick={() => {
                              setIsProfileMenuOpen(false);
                              handleLogout();
                            }}
                            className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-slate-700"
                          >
                            <LogOut className="w-4 h-4" />
                            <span className="text-sm font-medium">Sign Out</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
               </div>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex items-center xl:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="inline-flex items-center justify-center p-2 rounded-xl text-slate-500 hover:text-slate-900 hover:bg-slate-100 focus:outline-none transition-colors"
              >
                {isMobileMenuOpen ? <X className="block h-6 w-6" /> : <Menu className="block h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Desktop Navigation. Order: Newsfeed > Asset Radar > Catalyst Tracker > SOC >
              Conference > Additional resources > Admin.
              Wraps onto a second row instead of scrolling or overflowing. */}
          <nav className="hidden xl:flex flex-wrap items-center gap-0.5">
            <Link
              to="/newsfeed"
              title="Newsfeed"
              className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1 transition-all hover:bg-slate-100/50 rounded-full"
            >
              <Newspaper className="hidden 2xl:block w-3.5 h-3.5 text-indigo-600 shrink-0" />
              Newsfeed
            </Link>
            <Link
              to="/asset-radar"
              title="Asset Radar"
              className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1 transition-all hover:bg-slate-100/50 rounded-full"
            >
              <Radar className="hidden 2xl:block w-3.5 h-3.5 text-cyan-600 shrink-0" />
              Asset Radar
            </Link>
            <Link
              to="/catalyst-tracker"
              title="Catalyst Tracker"
              className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1 transition-all hover:bg-slate-100/50 rounded-full"
            >
              <GanttChartSquare className="hidden 2xl:block w-3.5 h-3.5 text-orange-600 shrink-0" />
              Catalyst Tracker
            </Link>
            <Link to="/reports" title="Standard of Care Reports" className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1 transition-all hover:bg-slate-100/50 rounded-full">
              <Activity className="hidden 2xl:block w-3.5 h-3.5 text-blue-600 shrink-0" />
              Standard of Care
            </Link>
            <Link
              to="/conference-abstracts"
              title="Conference Abstracts"
              className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap inline-flex items-center gap-1 transition-all hover:bg-slate-100/50 rounded-full"
            >
              <Calendar className="hidden 2xl:block w-3.5 h-3.5 text-violet-600 shrink-0" />
              Conference Abstracts
            </Link>
            <div className="relative resources-menu-container shrink-0">
              <button
                type="button"
                onClick={() => setIsResourcesMenuOpen((v) => !v)}
                onMouseEnter={() => setIsResourcesHovered(true)}
                onMouseLeave={() => setIsResourcesHovered(false)}
                title="Additional resources"
                className="shrink-0 px-1.5 py-1.5 whitespace-nowrap inline-flex items-center gap-1 transition-all rounded-full"
                // Pinned inline so this exactly matches its sibling nav links regardless of whatever
                // else is loaded on the page: a <button> here (needed for the dropdown) doesn't pick up
                // browser default text styling the same way the surrounding <a> links do, and in
                // practice something on this page has been winning over the plain Tailwind classes —
                // inline styles can't lose that fight.
                style={{
                  color: isResourcesHovered ? '#0f172a' : '#64748b',
                  backgroundColor: isResourcesHovered ? 'rgba(241,245,249,0.5)' : 'transparent',
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Inter, sans-serif',
                  lineHeight: 'normal',
                }}
              >
                <FolderOpen className="hidden 2xl:block w-3.5 h-3.5 text-fuchsia-600 shrink-0" />
                Additional resources
                <ChevronDown className="w-3 h-3 shrink-0" />
              </button>
              {isResourcesMenuOpen && (
                <div className="absolute left-0 mt-2 w-56 rounded-xl shadow-xl ring-1 ring-black/5 overflow-hidden bg-white/95 backdrop-blur-sm z-50">
                  <div className="py-1">
                    <Link to="/fda-approvals" onClick={() => setIsResourcesMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-slate-700">
                      <BadgeCheck className="w-4 h-4 text-sky-600 shrink-0" />
                      <span className="text-sm font-medium">FDA Approvals</span>
                    </Link>
                    <Link to="/clinical-publications" onClick={() => setIsResourcesMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-slate-700">
                      <BookOpen className="w-4 h-4 text-amber-600 shrink-0" />
                      <span className="text-sm font-medium">Journal Publications</span>
                    </Link>
                    <Link to="/prime-reports" onClick={() => setIsResourcesMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-slate-700">
                      <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span className="text-sm font-medium">Archive</span>
                    </Link>
                    <Link to="/trial-tracker" onClick={() => setIsResourcesMenuOpen(false)} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-slate-700">
                      <Bell className="w-4 h-4 text-rose-600 shrink-0" />
                      <span className="text-sm font-medium">Trial Tracker</span>
                    </Link>
                  </div>
                </div>
              )}
            </div>
            {isSuperuser && (
              <Link to="/superuser" className="shrink-0 text-slate-500 hover:text-slate-900 px-1.5 py-1.5 text-[12px] font-semibold whitespace-nowrap transition-all hover:bg-slate-100/50 rounded-full">
                Admin
              </Link>
            )}
          </nav>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="xl:hidden border-t border-gray-100/50 bg-white/95 backdrop-blur-xl absolute w-full shadow-lg">
          <div className="px-4 pt-4 pb-6 space-y-2">
            <Link to="/newsfeed" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Newsfeed</Link>
            <Link to="/asset-radar" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Asset Radar</Link>
            <Link to="/catalyst-tracker" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Catalyst Tracker</Link>
            <Link to="/reports" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Standard of Care</Link>
            <Link to="/conference-abstracts" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Conference Abstracts</Link>
            <p className="px-4 pt-3 pb-1 text-[11px] font-semibold uppercase tracking-widest text-slate-400">Additional resources</p>
            <Link to="/fda-approvals" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">FDA Approvals</Link>
            <Link to="/clinical-publications" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Journal Publications</Link>
            <Link to="/prime-reports" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Archive</Link>
            <Link to="/trial-tracker" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Trial Tracker</Link>
            {isSuperuser && (
              <Link to="/superuser" className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors">Admin</Link>
            )}
            <Link to="/account" onClick={() => setIsMobileMenuOpen(false)} className="block px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-2">
              <Settings className="w-4 h-4" /> Account Settings
            </Link>
            <button onClick={() => { setIsMobileMenuOpen(false); handleLogout(); }} className="w-full text-left px-4 py-3 rounded-xl text-base font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 transition-colors flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Sign Out
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
